import { DotMatrix } from '@clickguard/design-system'
import { GLoading } from 'components/basic-blocks'
import { MainLayout } from 'layout'
import { AdminHome } from 'pages/admin/home-page'
import { ContentAdminPage } from 'pages/admin/content/content-page'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useParams
} from 'react-router-dom'
import {
	useAccountStore,
	useAdminStore,
	useAssetStore,
	useUserStore
} from 'store'
import { publicRoutes } from 'utils/public-routes'
import { useShallow } from 'zustand/react/shallow'
import { isNoClickGuardAffiliate } from '../utils/user'
import { AdminAccessTypes, hasAdminAccess } from '../utils/admin'
import {
	getFromLocalStorage,
	removeFromLocalStorage,
	setItemsToLocalStorage
} from '../utils'
import { AppBase } from './app-base'
import { DashboardPage } from './workspace/asset/dashboard'
import { PageNotFound } from './pagenotfound'
import { AdminBase } from './admin/admin-base'
import { AdminUserPage } from './admin/entites/user/user-page'
import { AdminAccountsPage } from './admin/entites/accounts'
import { ProfileSetting } from './user/settings/profile-setting'
import { SecuritySetting } from './user/settings/security-setting'
import { AssetBase } from './workspace/asset/asset-base'
import { AccountOverviewPage } from './account-overview'
import { NotificationSetting } from './user/settings/notification-setting'
import { Settings } from './user/settings/settings'
import { OffersSetting } from './user/settings/offers-setting'
import { ClicksPage } from './workspace/asset/clicks'
import { BlacklistPage } from './workspace/asset/blacklist'
import { ExclusionsPage } from './workspace/asset/exclusions'
import { AnalyticsReportPage } from './workspace/asset/analytics-report'
import { AdminAccountPage } from './admin/entites/account/account-page'
import { AdminPPCAccountPage } from './admin/entites/ppc-account-page'
import { AdminPPCAccountsPage } from './admin/entites/ppc-accounts-page'
import { AdminUsersPage } from './admin/entites/users'
import { AdminWebsitePage } from './admin/entites/website-page'
import { AdminWebsitesPage } from './admin/entites/websites-page'
import { AdminMetricsPage } from './admin/metrics'
import { AdminDomainsPage } from './admin/references/domains-page'
import { AdminSurveysPage } from './admin/surveys'
import { AdminPPCAccountsReferencesPage } from './admin/references/ppc-accounts-references-page'
import { AdminRevenuePage } from './admin/revenue'
import { ConnectionsPage } from './user/settings/connections/connections-page'
import {
	AdAccountsRedirect,
	AuthorizationsRedirect
} from './user/settings/connections/connections-redirects'
import { CustomRulesPage } from './workspace/asset/custom-rules'
import { AiProtectionPage } from './workspace/asset/ai-protection'
import { ProtectionRedirect } from './workspace/asset/protection'
import { RefundsPage } from './workspace/asset/refunds'
import { LeadFormsPage } from './workspace/asset/lead-forms'
// Dev-only preview shell for the playground; only mounted when NODE_ENV !== 'production'.
import { PreviewPlayground } from './preview-playground'
import { ScheduledReportsPage } from './workspace/asset/scheduled-reports-v2'
import { AssetSettingsPage } from './workspace/asset/settings-page'
import { TrackingSection } from './workspace/asset/settings-page/tracking-section'
import { TrackingSectionV2 } from './workspace/asset/settings-page/tracking-section/tracking-section-v2'
import { ConversionTrackingSection } from './workspace/asset/settings-page/tracking-section/conversion-tracking-section'
import { BillingPage } from './workspace/billing'
import { PreferencesSettingsPage } from './workspace/preferences/settings-page'
import { TeamAccessPage } from './workspace/team-access'
import { Affiliate } from './user/affiliate/view'
import { AdminTeam } from './admin/team'
import { LegacyUser } from './admin/legacy-user/legacy-user'
import { useNavigateWithParams } from '../hooks/use-navigate-with-params'
import { appendMarketingParams } from '../utils/marketing-params'
import { useFeatureFlag } from 'services/feature-flags'
import { PaymentIssueDetected } from 'components/payment-issue-detected'
import { useWorkspaceAccess, WorkspaceAccess } from 'hooks/use-workspace-access'

// Redirect the retired /analytics and /threat routes to the new report, keeping
// the current query string — the target page reads from/to and adAccounts/
// campaigns off the URL, so dropping it would reset a deep link to defaults.
const RedirectToAnalyticsReports = () => {
	const { search } = useLocation()
	return <Navigate to={{ pathname: '../analytics-reports', search }} replace />
}

const RequireAccess = ({
	children,
	fallback,
	alsoOpen = []
}: {
	children: ReactElement
	fallback: 'dashboard' | 'workspace-settings' | 'profile' | 'home'
	alsoOpen?: WorkspaceAccess[]
}) => {
	const { access, ready } = useWorkspaceAccess()
	const { accountId, assetId } = useParams()
	if (!ready) return <GLoading />
	if (access === 'full' || alsoOpen.includes(access)) return children
	const to =
		fallback === 'dashboard'
			? `/workspace/${accountId}/asset/${assetId}`
			: fallback === 'workspace-settings'
			? `/settings/workspace/${accountId}/preferences`
			: fallback === 'profile'
			? '/settings/user/profile'
			: '/'
	return <Navigate to={to} replace />
}

export const MainPage = () => {
	const navigate = useNavigateWithParams()
	const isServerSideTrackingEnabled = useFeatureFlag(
		'server_side_tracking_enabled'
	)
	const { account, setAccount, wantsToSeeDemoData, accounts, getAllAccounts } =
		useAccountStore(
			useShallow((s) => ({
				account: s.account,
				setAccount: s.setAccount,
				wantsToSeeDemoData: s.wantsToSeeDemoData,
				accounts: s.accounts,
				getAllAccounts: s.getAllAccounts
			}))
		)
	const { asset, setAsset, setAssetForId, setDemoMode, allAssets } =
		useAssetStore(
			useShallow((s) => ({
				asset: s.asset,
				setAsset: s.setAsset,
				setAssetForId: s.setAssetForId,
				setDemoMode: s.setDemoMode,
				allAssets: s.allAssets
			}))
		)
	const imperId = useAdminStore((s) => s.imperId)
	const user = useUserStore((s) => s.user)

	const [loading, setLoading] = useState(true)

	const path = window.location.pathname.split('/')
	const { accountId, assetId, isPublicRoute, isInSettings } = useMemo(() => {
		// Real ids are `acc`/`ast` + a 10-char alphanumeric ShortUniqueId (generateId,
		// core/lib/utils/uuid.ts) — never hyphenated. Match that exact shape rather than
		// a loose `startsWith('acc')`, so id-less top-level route segments that merely
		// begin with those letters (e.g. `account-overview`) aren't misread as ids and
		// bounced to page-not-found on refresh.
		const accountId =
			path.filter(
				(link) => /^acc[A-Za-z0-9]+$/.test(link) || link === 'workspace-demo'
			)[0] || getFromLocalStorage('selected_account')
		const assetId =
			path.filter(
				(link) => /^ast[A-Za-z0-9]+$/.test(link) || link === 'website-demo'
			)[0] || getFromLocalStorage('selected_asset')
		const isInSettings = path.includes('settings')
		const isPublicRoute = publicRoutes.find((route) =>
			window.location.pathname.startsWith(route)
		)
		return { accountId, assetId, isPublicRoute, isInSettings }
	}, [path])

	useEffect(() => {
		// The auth-free public routes (the emailed report link, /report/:id) mount
		// this page too, and App never sets an API token there — so this call would
		// 401 and surface as a stray "This request is unauthorized" toast.
		if (isPublicRoute) return
		const loadAccounts = async () => {
			await getAllAccounts()
		}
		loadAccounts()
		// eslint-disable-next-line
	}, [])

	useEffect(() => {
		setLoading(true)
	}, [imperId])

	useEffect(() => {
		async function checkWorkspace() {
			// await Promise.all([accounts, getAllAccounts()])
			if (
				wantsToSeeDemoData ||
				path[1] === 'admin' ||
				path[1] === 'docs' ||
				path[1] === 'changelog' ||
				isPublicRoute
			) {
				setLoading(false)
				return
			}
			if (isNoClickGuardAffiliate(user)) {
				navigate('/workspace/affiliate?tab=about')
				setLoading(false)
				return
			}
			const noAccounts = !accounts || accounts.length === 0
			if (noAccounts && !imperId) {
				navigate('/start/standard')
				setLoading(false)
				return
			}

			if (accountId === 'workspace-demo') {
				setDemoMode(true)
				setAccount(null)
				await setAssetForId('workspace-demo', 'website-demo')
				setLoading(false)
				return
			}
			if (assetId === 'website-demo' || assetId === undefined) {
				setDemoMode(true)
			}

			const selectedAccount = accountId
				? accounts.find((a) => a.id === accountId)
				: accounts[0]

			if (!selectedAccount) {
				setAccount(null)
				setAsset(null)
				setLoading(false)
				removeFromLocalStorage(['selected_asset', 'selected_account'])
				navigate('page-not-found')
				return
			}

			setAccount(selectedAccount)

			if (assetId === 'website-demo') {
				setLoading(false)
				await setAssetForId('workspace-demo', 'website-demo')
				return
			}

			const workspaceWebsites = allAssets.filter(
				(a) => a.account_id === selectedAccount.id
			)

			if (workspaceWebsites.length === 0) {
				setLoading(false)
				setDemoMode(true)
				await setAssetForId('workspace-demo', 'website-demo')
				return
			}

			const selectedWebsite = assetId
				? workspaceWebsites?.find((a) => a.id === assetId)
				: workspaceWebsites[0]

			if (!selectedWebsite) {
				setLoading(false)
				setAsset(null)
				removeFromLocalStorage(['selected_asset', 'selected_account'])
				navigate('asset-not-found')
				return
			}

			setAsset(selectedWebsite)
			await setAssetForId(selectedWebsite.account_id, selectedWebsite.id)
			setItemsToLocalStorage([
				{ key: 'selected_asset', value: selectedWebsite.id },
				{ key: 'selected_account', value: selectedWebsite.account_id }
			])
			setLoading(false)
		}

		checkWorkspace()
	}, [allAssets.length])

	// Check if admin is bypassing paywall during impersonation
	const adminBypassPaywall =
		getFromLocalStorage('ad_bp_aze_imper_user') === 'true'
	const isAdminImpersonating = useAdminStore((s) => s.impersonation)

	// Only bypass if admin is impersonating and bypass flag is set
	const shouldBypassPaywall = adminBypassPaywall && isAdminImpersonating

	if (loading) {
		return (
			<div className="h-96 flex flex-col justify-end items-center text-center space-y-4">
				<DotMatrix size="lg" />
				<span className="w-full text-center text-t-secondary">
					Fetching account information...
				</span>
			</div>
		)
	}

	if (
		account &&
		account.payment_overdue === true &&
		path[1] !== 'docs' &&
		path[1] !== 'changelog' &&
		account.billing?.subscription?.status === 'ACTIVE' &&
		!shouldBypassPaywall
	) {
		return <PaymentIssueDetected />
	}

	return (
		<MainLayout>
			<Routes>
				{/* Dev-only: auth-free preview of the playground shell, seeded with a mock
				    workspace/website/ad-accounts/campaigns so it renders without a backend.
				    Path includes /asset/ so the breadcrumb shows ad-account + campaign selectors.
				    Guarded by NODE_ENV so it never exists in a production build. */}
				{process.env.NODE_ENV !== 'production' && (
					<Route
						path="/preview/asset/playground"
						element={<PreviewPlayground />}
					/>
				)}
				{/* The public Analytics report link (/report/:id) is routed in App.tsx,
				    not here: this tree is wrapped in MainLayout (Sidebar + Navbar). */}
				<Route path="/" element={<AppBase />}>
					<Route
						index
						element={
							account?.id && asset?.id ? (
								<Navigate
									to={appendMarketingParams(
										`workspace/${account.id}/asset/${asset.id}`
									)}
									replace
								/>
							) : null
						}
					/>
					<Route path="workspace/affiliate" element={<Affiliate />} />
					<Route
						path="account-overview"
						element={
							<RequireAccess fallback="home">
								<AccountOverviewPage />
							</RequireAccess>
						}
					/>
					<Route path="*" element={<PageNotFound />} />
					<Route path="/settings" element={<Settings />}>
						<Route
							path="workspace/:accountId/asset/:assetId/site-preference"
							element={
								<RequireAccess
									fallback="workspace-settings"
									alsoOpen={['no-ad-account', 'cancelled']}
								>
									<AssetSettingsPage />
								</RequireAccess>
							}
						/>
						{isServerSideTrackingEnabled ? (
							<Route
								path="workspace/:accountId/asset/:assetId/tracking-code"
								element={
									<RequireAccess
										fallback="workspace-settings"
										alsoOpen={['cancelled']}
									>
										<TrackingSectionV2 />
									</RequireAccess>
								}
							/>
						) : (
							<Route
								path="workspace/:accountId/asset/:assetId/tracking-code"
								element={
									<RequireAccess
										fallback="workspace-settings"
										alsoOpen={['cancelled']}
									>
										<TrackingSection />
									</RequireAccess>
								}
							/>
						)}
						{isServerSideTrackingEnabled && (
							<Route
								path="workspace/:accountId/asset/:assetId/conversion-tracking"
								element={
									<RequireAccess
										fallback="workspace-settings"
										alsoOpen={['cancelled']}
									>
										<ConversionTrackingSection />
									</RequireAccess>
								}
							/>
						)}
						<Route
							path="workspace/:accountId/asset/:assetId/connections"
							element={
								<RequireAccess
									fallback="workspace-settings"
									alsoOpen={['cancelled']}
								>
									<ConnectionsPage />
								</RequireAccess>
							}
						/>
						<Route
							path="workspace/:accountId/preferences"
							element={<PreferencesSettingsPage />}
						/>
						<Route
							path="workspace/:accountId/team-access"
							element={<TeamAccessPage />}
						/>
						<Route
							path="workspace/:accountId/billing"
							element={
								<RequireAccess
									fallback="workspace-settings"
									alsoOpen={['no-setup', 'no-ad-account', 'cancelled']}
								>
									<BillingPage />
								</RequireAccess>
							}
						/>{' '}
						<Route path="user/notification" element={<NotificationSetting />} />
						<Route path="user/profile" element={<ProfileSetting />} />
						<Route
							path="user/offers"
							element={
								<RequireAccess
									fallback="profile"
									alsoOpen={['no-setup', 'no-ad-account', 'cancelled']}
								>
									<OffersSetting />
								</RequireAccess>
							}
						/>
						<Route path="user/security" element={<SecuritySetting />} />
					</Route>
					<Route
						path="workspace/:accountId/connections"
						element={<AuthorizationsRedirect />}
					/>
					<Route
						path="/workspace/:accountId/asset/:assetId"
						element={<AssetBase />}
					>
						<Route index element={<DashboardPage />} />
						<Route
							path="clicks"
							element={
								<RequireAccess fallback="dashboard">
									<ClicksPage />
								</RequireAccess>
							}
						/>
						<Route
							path="exclusions"
							element={
								<RequireAccess fallback="dashboard" alsoOpen={['cancelled']}>
									<ExclusionsPage />
								</RequireAccess>
							}
						/>
						<Route
							path="reports"
							element={
								<RequireAccess fallback="dashboard">
									<ScheduledReportsPage />
								</RequireAccess>
							}
						/>
						<Route
							path="refunds"
							element={
								<RequireAccess fallback="dashboard">
									<RefundsPage />
								</RequireAccess>
							}
						/>
						<Route path="lead-forms" element={<LeadFormsPage />} />
						{/* DEV-2422 W13: the old combined page while the flag is off, a
						    redirect to AI Protection once it is on. */}
						<Route
							path="protection"
							element={
								<RequireAccess fallback="dashboard">
									<ProtectionRedirect />
								</RequireAccess>
							}
						/>
						{/* DEV-2403. Mounted like every other route; the PAGE checks
						    `custom_rules_v2` and redirects, as RefundsPage does. */}
						<Route
							path="custom-rules"
							element={
								<RequireAccess fallback="dashboard">
									<CustomRulesPage />
								</RequireAccess>
							}
						/>
						{/* DEV-2422, same flag and same cutover as Custom Rules above. */}
						<Route
							path="ai-protection"
							element={
								<RequireAccess fallback="dashboard">
									<AiProtectionPage />
								</RequireAccess>
							}
						/>
						<Route
							path="blacklist"
							element={
								<RequireAccess fallback="dashboard" alsoOpen={['cancelled']}>
									<BlacklistPage />
								</RequireAccess>
							}
						/>
						<Route
							path="analytics-reports"
							element={
								<RequireAccess fallback="dashboard">
									<AnalyticsReportPage />
								</RequireAccess>
							}
						/>
						{/* Old Analytics + Threat pages were removed (DEV-2377); redirect
						    their URLs to the new report so existing links/bookmarks survive.
						    Carry the query string so a bookmarked ?from/to/adAccounts/campaigns
						    deep link keeps its date range and scope instead of resetting. */}
						<Route path="analytics" element={<RedirectToAnalyticsReports />} />
						<Route path="threat" element={<RedirectToAnalyticsReports />} />
						<Route path="ppc-accounts" element={<AdAccountsRedirect />} />
					</Route>
				</Route>
				<Route path="/admin" element={<AdminBase />}>
					<Route index element={<AdminHome />} />
					<Route path="docs/*" element={<ContentAdminPage />} />
					{hasAdminAccess(user, AdminAccessTypes.view_metrics) ? (
						<>
							<Route path="metrics" element={<AdminMetricsPage />} />
							<Route path="revenue" element={<AdminRevenuePage />} />
						</>
					) : (
						<></>
					)}
					{hasAdminAccess(user, AdminAccessTypes.view_cg_staff) ? (
						<Route path="team" element={<AdminTeam />} />
					) : (
						<></>
					)}

					<Route path="legacy" element={<LegacyUser />} />
					<Route path="users" element={<AdminUsersPage />} />
					<Route path="user/:userId" element={<AdminUserPage />} />
					<Route path="subscriptions" element={<AdminAccountsPage />} />
					<Route
						path="subscription/:accountId"
						element={<AdminAccountPage />}
					/>
					<Route path="websites" element={<AdminWebsitesPage />} />
					<Route path="website/:assetId" element={<AdminWebsitePage />} />
					<Route path="ppc-accounts" element={<AdminPPCAccountsPage />} />
					<Route
						path="ppc-account/:ppcAccountId"
						element={<AdminPPCAccountPage />}
					/>
					<Route path="references/domains" element={<AdminDomainsPage />} />
					<Route path="surveys" element={<AdminSurveysPage />} />
					<Route
						path="references/ppc-accounts"
						element={<AdminPPCAccountsReferencesPage />}
					/>
				</Route>
				<Route path="*" element={<PageNotFound />} />
			</Routes>
		</MainLayout>
	)
}
